import { Box, Typography } from '@mui/material';
import { formatMoney } from '../cashbacks/formatMoney';
import type { Currency } from '../dataProvider/types';
import type { EarningsTrendPoint } from './earningsModel';

/**
 * The two-line trend at the foot of a band: confirmed margin and contribution
 * over time, so the gap between them reads as welcome spend. Rendered as a
 * dependency-free inline SVG (there is no charting library in the project), with
 * a legend and an accessible data table beneath it.
 *
 * This renders ONLY when the band passed the trend gate (the parent passes null
 * otherwise and renders nothing), so there is no "not enough data" placeholder.
 */

const WIDTH = 640;
const HEIGHT = 200;
const PAD = { top: 16, right: 16, bottom: 28, left: 16 };
const INNER_W = WIDTH - PAD.left - PAD.right;
const INNER_H = HEIGHT - PAD.top - PAD.bottom;
const CONFIRMED_COLOR = '#1565c0';
const CONTRIBUTION_COLOR = '#2e7d32';

const LegendKey = ({ color, label }: { color: string; label: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
    <Box
      sx={{ width: 14, height: 3, bgcolor: color, borderRadius: 1 }}
      aria-hidden
    />
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

export const EarningsTrend = ({
  points,
  currency,
}: {
  points: EarningsTrendPoint[];
  currency: Currency;
}) => {
  const values = points.flatMap((p) => [p.confirmedMargin, p.contribution]);
  const min = Math.min(0, ...values);
  const max = Math.max(0, ...values);
  const span = max - min || 1;
  const step = points.length > 1 ? INNER_W / (points.length - 1) : 0;

  const x = (i: number): number => PAD.left + step * i;
  const y = (value: number): number =>
    PAD.top + INNER_H - ((value - min) / span) * INNER_H;
  const path = (value: (p: EarningsTrendPoint) => number): string =>
    points
      .map(
        (p, i) =>
          `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(value(p)).toFixed(1)}`,
      )
      .join(' ');

  return (
    <Box sx={{ mt: 2 }} aria-label="earnings trend">
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Confirmed margin and contribution over time
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
        <LegendKey color={CONFIRMED_COLOR} label="Confirmed margin" />
        <LegendKey color={CONTRIBUTION_COLOR} label="Contribution" />
      </Box>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        width="100%"
        height={HEIGHT}
        role="img"
        aria-label="Confirmed margin and contribution trend lines"
        style={{ maxWidth: WIDTH }}
      >
        {min < 0 && (
          <line
            x1={PAD.left}
            x2={WIDTH - PAD.right}
            y1={y(0)}
            y2={y(0)}
            stroke="#e0e0e0"
            strokeWidth={1}
          />
        )}
        <path
          d={path((p) => p.confirmedMargin)}
          fill="none"
          stroke={CONFIRMED_COLOR}
          strokeWidth={2}
        />
        <path
          d={path((p) => p.contribution)}
          fill="none"
          stroke={CONTRIBUTION_COLOR}
          strokeWidth={2}
        />
      </svg>

      {/* Accessible data table backing the lines (also exact for tests). */}
      <Box
        component="table"
        sx={{ width: '100%', mt: 1, borderCollapse: 'collapse' }}
      >
        <Box component="thead">
          <Box component="tr">
            <Box component="th" sx={{ textAlign: 'left', py: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Period
              </Typography>
            </Box>
            {points.map((p) => (
              <Box
                key={p.period}
                component="th"
                sx={{ textAlign: 'right', py: 0.5 }}
              >
                <Typography variant="caption" color="text.secondary">
                  {p.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          <Box component="tr">
            <Box component="td">
              <Typography variant="caption">Confirmed margin</Typography>
            </Box>
            {points.map((p) => (
              <Box key={p.period} component="td" sx={{ textAlign: 'right' }}>
                <Typography variant="caption">
                  {formatMoney(p.confirmedMargin, currency)}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box component="tr">
            <Box component="td">
              <Typography variant="caption">Contribution</Typography>
            </Box>
            {points.map((p) => (
              <Box key={p.period} component="td" sx={{ textAlign: 'right' }}>
                <Typography variant="caption">
                  {formatMoney(p.contribution, currency)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
